import { Injectable, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs/internal/Observable";
import { IReleaseNotesData, IReleaseNotesPayload } from "src/app/shared/types/interfaces/release-notes.interface";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ApiService } from "../api.service";
import { catchError, map, tap, throwError } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { UpdatePromptComponent } from "../../components/update-prompt/update-prompt.component";
import { AuthService } from "src/app/core/services/auth.service";

@Injectable({
  providedIn: "root",
})
export class ReleaseNotesService extends ApiService {
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);

  private url = `${this.api}/release-notes`;
  dialogConfig: any;

  constructor() {
    super();
  }

  /**
   * gets most recent app version
   * @returns most recent app version
   */
  public getAppVersion(): Observable<any> {
    const url = `${this.url}?action=R&version=latest`;
    this.authService.storeToken();
    let headers = this.getRequestHeaders();
    return this.httpClient.get<IReleaseNotesData>(url, { headers }).pipe(
      map((data: any) => this.transformReleaseNotesData(data.output.data[0])),
      catchError((e: ErrorEvent) => {
        if (e.error.error.includes("expired")) {
          this.authService.handleRedirectObservable().subscribe({
            next: result => {
              this.authService.storeToken();
              window.location.reload();
            },
            error: error => {
              console.error(error);
            },
          });
        }
        return throwError(() => e);
      })
    );
  }

  public openAppVersionDialog(): Observable<boolean> {
    const dialogRef = this.dialog.open(UpdatePromptComponent, {
      minWidth: "50%",
      // height: '70%',
      disableClose: true,
    });
    return dialogRef.afterClosed();
  }

  /**
   * gets history of release notes.
   * currently hardcoded to return only previous 3 release notes.
   * @returns list of release notes
   */
  public getReleaseNotes(): Observable<IReleaseNotesData[]> {
    const url = `${this.url}?action=R`;
    this.authService.storeToken();
    const headers = this.getRequestHeaders();
    return this.httpClient.get<any[]>(url, { headers }).pipe(
      map((res: any) => res.output.data.map((item: any) => this.transformReleaseNotesData(item))),
      catchError((e: ErrorEvent) => {
        if (e.error.error.includes("expired")) {
          this.authService.handleRedirectObservable().subscribe({
            next: result => {
              this.authService.storeToken();
              window.location.reload();
            },
            error: error => {
              console.error(error);
            },
          });
        }
        return throwError(() => e);
      })
    );
  }

  /**
   * creates a release note.
   *
   * @returns
   */
  public createReleaseNote(payload: IReleaseNotesPayload): Observable<any> {
    const url = `${this.url}?action=C`;
    this.authService.storeToken();
    const headers = this.getRequestHeaders();
    return this.httpClient.post<IReleaseNotesData>(url, payload, { headers }).pipe(
      catchError((e: ErrorEvent) => {
        if (e.error.error.includes("expired")) {
          this.authService.handleRedirectObservable().subscribe({
            next: result => {
              this.authService.storeToken();
              window.location.reload();
            },
            error: error => {
              console.error(error);
            },
          });
        }
        return throwError(() => e);
      })
    );
  }

  /**
   * deletes a release note by version.
   *
   * @returns
   */
  public deleteReleaseNote(version: string): Observable<any> {
    const url = `${this.url}?action=D`;
    this.authService.storeToken();
    const headers = this.getRequestHeaders();
    const payload = {
      version: version,
    };
    return this.httpClient.post<any>(url, JSON.stringify(payload), { headers }).pipe(
      catchError((e: ErrorEvent) => {
        if (e.error.error.includes("expired")) {
          this.authService.handleRedirectObservable().subscribe({
            next: result => {
              this.authService.storeToken();
              window.location.reload();
            },
            error: error => {
              console.error(error);
            },
          });
        }
        return throwError(() => e);
      })
    );
  }

  private transformReleaseNotesData(data: any): IReleaseNotesData {
    return {
      version: data.version,
      releaseDate: data.release_date,
      description: {
        bugFixes: data.description.bug_fixes,
        newFeatures: data.description.new_features,
      },
    };
  }

  private getRequestHeaders() {
    const authToken = localStorage.getItem("tokenKey");
    const headers = new HttpHeaders({
      "X-Auth-Token": authToken!,
      "content-type": "application/json",
      "Cache-Control": "no-cache",
      "Access-Control-Allow-Origin": "*",
    });
    return headers;
  }
}
